import { ConfirmationDialog } from "@biom3/react";
import type { Content } from "@prismicio/client";
import { Fit, Layout, useRive } from "@rive-app/react-canvas";
import { type ComponentProps, Fragment } from "react";

import { MODAL_HEIGHT } from "../../../constants";
import { ParallaxWrapper } from "../core/ParallaxWrapper";
import type { QuestLevelModal } from "./types";
import { parseTemplate } from "./utils";

export const CustomRive = ({
  src,
  ...props
}: { src: string } & ComponentProps<"canvas">) => {
  const { RiveComponent, rive } = useRive({
    src,
    stateMachines: ["State Machine 1"],
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover }),
    onLoad: () => {
      rive?.resizeDrawingSurfaceToCanvas();
    },
  });

  return <RiveComponent {...props} />;
};

export type QuestModalProps = QuestLevelModal & {
  visible: boolean;
  onClose: () => void;
  game: Content.GameDocument;
};

export const QuestModal = ({
  visible,
  onClose,
  title,
  description,
  ctaLabel,
  foregroundImage,
  game,
}: QuestModalProps) => {
  if (!title || !description || !foregroundImage) {
    return null;
  }

  const placeholderMap = {
    "<name>": (
      <Fragment key={`nextQuest-${game.data.name}`}>{game.data.name}</Fragment>
    ),
  };

  return (
    <ConfirmationDialog visible={visible}>
      <ConfirmationDialog.Content sx={{ h: MODAL_HEIGHT }}>
        <ConfirmationDialog.Content.RiveBox
          rc={<CustomRive src="/rive/quest_level_celebration.riv" />}
          testId="riveBox"
        />
        <ConfirmationDialog.Content.Image
          use={
            <ParallaxWrapper
              intensity={0.02}
              sx={{
                position: "absolute",
                left: "0",
                right: "0",
                top: "0",
                bottom: "0",
                pointerEvents: "none",
                "@media (max-height: 400px)": {
                  d: "none",
                },
              }}
            >
              <img
                src={foregroundImage}
                alt={title}
                style={{
                  top: "-50px",
                  left: "50%",
                  position: "absolute",
                  height: "340px",
                  aspectRatio: "unset",
                  transform: "translateX(-50%)",
                  pointerEvents: "none",
                }}
              />
            </ParallaxWrapper>
          }
          sx={{
            top: "-50px",
            left: "50%",
            pos: "absolute",
            h: "340px",
            aspectRatio: "unset",
            transform: "translateX(-50%)",
            pointerEvents: "none",
            "@media (max-height: 400px)": {
              d: "none",
            },
          }}
        />
        <ConfirmationDialog.Content.Title sx={{ mt: "40px" }}>
          {parseTemplate(title || "", placeholderMap)}
        </ConfirmationDialog.Content.Title>
        <ConfirmationDialog.Content.Caption sx={{ mt: "10px" }}>
          {parseTemplate(description || "", placeholderMap)}
        </ConfirmationDialog.Content.Caption>

        <ConfirmationDialog.Content.Button
          size="large"
          onClick={async () => {
            onClose?.();
          }}
        >
          {ctaLabel ? ctaLabel : "Done"}
        </ConfirmationDialog.Content.Button>
        <ConfirmationDialog.Content.CloseButtCon
          onClick={async () => {
            onClose?.();
          }}
        />
      </ConfirmationDialog.Content>
    </ConfirmationDialog>
  );
};
